import { PlusIcon } from '@persona-id/design-tokens/icons';
import {
  ArrayFieldTemplateItemType,
  ArrayFieldTemplateProps,
  getTemplate,
  getUiOptions,
  RJSFSchema,
  StrictRJSFSchema,
} from '@rjsf/utils';
import pluralize from 'pluralize';
import styled from 'styled-components';

import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';

const BackgroundCard = styled(Card)`
  background-color: ${({ theme }) => theme.color.background.secondary};
`;

const HeaderContainer = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
`;

/** The `ArrayFieldTemplate` component is the template used to render all items in an array.
 *
 * @param props - The `ArrayFieldTemplateItemType` props for the component
 */
export function ArrayFieldTemplate<T = any, S extends StrictRJSFSchema = RJSFSchema, F = any>(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  props: ArrayFieldTemplateProps<T, S, F>,
) {
  const {
    canAdd,
    disabled,
    idSchema,
    uiSchema,
    items,
    onAddClick,
    readonly,
    registry,
    required,
    schema,
    title,
  } = props;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const uiOptions = getUiOptions<T, S, F>(uiSchema);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const ArrayFieldDescriptionTemplate = getTemplate<'ArrayFieldDescriptionTemplate', T, S, F>(
    'ArrayFieldDescriptionTemplate',
    registry,
    uiOptions,
  );
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const ArrayFieldItemTemplate = getTemplate<'ArrayFieldItemTemplate', T, S, F>(
    'ArrayFieldItemTemplate',
    registry,
    uiOptions,
  );
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const ArrayFieldTitleTemplate = getTemplate<'ArrayFieldTitleTemplate', T, S, F>(
    'ArrayFieldTitleTemplate',
    registry,
    uiOptions,
  );

  const buttonText = title ? `Add ${pluralize.singular(title)}` : 'Add entry';

  return (
    <fieldset id={idSchema.$id}>
      <HeaderContainer>
        <div>
          <ArrayFieldTitleTemplate
            idSchema={idSchema}
            title={uiOptions.title || title}
            required={required}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
          <ArrayFieldDescriptionTemplate
            idSchema={idSchema}
            description={uiOptions.description || schema.description}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
        </div>
        <div>
          {canAdd && !readonly && (
            <Button
              prefixIcon={<PlusIcon />}
              width='auto'
              variant='basic'
              onClick={onAddClick}
              disabled={disabled}
            >
              {buttonText}
            </Button>
          )}
        </div>
      </HeaderContainer>
      <Spacer height={1} />
      <BackgroundCard>
        <Card.Body>
          {items.length > 0 ? (
            items
              .reverse()
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              .map(({ key, ...itemProps }: ArrayFieldTemplateItemType<T, S, F>) => (
                <ArrayFieldItemTemplate key={key} {...itemProps} />
              ))
          ) : (
            <Paragraph>No entries</Paragraph>
          )}
        </Card.Body>
      </BackgroundCard>
    </fieldset>
  );
}
